export class VideoManager {
  private static instance: VideoManager;
  private currentPlayingVideo: HTMLVideoElement | null = null;
  private videoElements: Map<string, HTMLVideoElement> = new Map();
  private intersectionObserver: IntersectionObserver | null = null;
  private isEnabled: boolean = true;

  private constructor() {
    this.setupIntersectionObserver();
    this.setupGlobalEventListeners();
  }

  public static getInstance(): VideoManager {
    if (!VideoManager.instance) {
      VideoManager.instance = new VideoManager();
    }
    return VideoManager.instance;
  }

  private setupIntersectionObserver(): void {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (!this.isEnabled) return;

        entries.forEach((entry) => {
          const video = entry.target as HTMLVideoElement;
          const videoId = video.dataset.videoId;

          if (!entry.isIntersecting && videoId) {
            // Video is out of viewport, pause it
            this.pauseVideo(videoId);
          }
        });
      },
      {
        threshold: 0.5, // Video must be 50% visible to be considered in viewport
        rootMargin: "0px 0px -10% 0px", // Consider video out of viewport when bottom 10% is hidden
      }
    );
  }

  private setupGlobalEventListeners(): void {
    // Pause all videos when page becomes hidden (user switches tabs)
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        this.pauseAllVideos();
      }
    });

    // Pause all videos when window loses focus
    window.addEventListener("blur", () => {
      this.pauseAllVideos();
    });

    // Handle keyboard shortcuts
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        this.pauseAllVideos();
      }
    });
  }

  public registerVideo(videoId: string, videoElement: HTMLVideoElement): void {
    if (!this.isEnabled) return;

    this.videoElements.set(videoId, videoElement);
    videoElement.dataset.videoId = videoId;

    // Observe the video for viewport changes
    if (this.intersectionObserver) {
      this.intersectionObserver.observe(videoElement);
    }

    // Add play event listener to stop other videos when this one starts playing
    const playHandler = () => {
      this.onVideoPlay(videoId);
    };
    videoElement.addEventListener("play", playHandler);

    // Add error handling
    videoElement.addEventListener("error", () => {
      console.warn(`Video error for video ID: ${videoId}`);
      this.unregisterVideo(videoId);
    });
  }

  public unregisterVideo(videoId: string): void {
    const video = this.videoElements.get(videoId);
    if (video) {
      if (this.intersectionObserver) {
        this.intersectionObserver.unobserve(video);
      }

      // Remove event listeners
      video.removeEventListener("play", () => this.onVideoPlay(videoId));

      this.videoElements.delete(videoId);

      // If this was the current playing video, clear the reference
      if (this.currentPlayingVideo === video) {
        this.currentPlayingVideo = null;
      }
    }
  }

  private onVideoPlay(videoId: string): void {
    if (!this.isEnabled) return;

    // Stop all other videos when one starts playing
    this.videoElements.forEach((video, id) => {
      if (id !== videoId && !video.paused) {
        video.pause();
      }
    });

    this.currentPlayingVideo = this.videoElements.get(videoId) || null;
  }

  public playVideo(videoId: string): void {
    if (!this.isEnabled) return;

    const video = this.videoElements.get(videoId);
    if (video) {
      // Stop current playing video if different
      if (this.currentPlayingVideo && this.currentPlayingVideo !== video) {
        this.currentPlayingVideo.pause();
      }

      video.play().catch((error) => {
        console.warn("Failed to play video:", error);
      });

      this.currentPlayingVideo = video;
    }
  }

  public pauseVideo(videoId: string): void {
    const video = this.videoElements.get(videoId);
    if (video && !video.paused) {
      video.pause();

      if (this.currentPlayingVideo === video) {
        this.currentPlayingVideo = null;
      }
    }
  }

  public pauseAllVideos(): void {
    this.videoElements.forEach((video) => {
      if (!video.paused) {
        video.pause();
      }
    });
    this.currentPlayingVideo = null;
  }

  public getCurrentPlayingVideo(): HTMLVideoElement | null {
    return this.currentPlayingVideo;
  }

  public isVideoPlaying(videoId: string): boolean {
    const video = this.videoElements.get(videoId);
    return video ? !video.paused : false;
  }

  public getVideoCount(): number {
    return this.videoElements.size;
  }

  public enable(): void {
    this.isEnabled = true;
  }

  public disable(): void {
    this.isEnabled = false;
    this.pauseAllVideos();
  }

  public isManagerEnabled(): boolean {
    return this.isEnabled;
  }

  public destroy(): void {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
    this.pauseAllVideos();
    this.videoElements.clear();
    this.currentPlayingVideo = null;
    this.isEnabled = false;
  }
}

// Export singleton instance
export const videoManager = VideoManager.getInstance();
